import { createDefaultRegistry } from '@campfire/catalog';
import { resolveSettings, type OptionValue, type Seat } from '@campfire/engine';
import {
  connectInMemory,
  createClientSession,
  createHostSession,
  type ClientSession,
  type GameInfo,
  type HostSession,
  type Role,
  type StateMessage,
} from '@campfire/net';
import { connectSocket, type SocketLink } from './connection.ts';
import { confirmButton, h, randomId } from './dom.ts';
import { mountChat, type ChatPanel } from './chat.ts';
import { signInStrip } from './signin.ts';
import { shortAge } from './age.ts';
import { mountTextSizeButton } from './textsize.ts';
import { mountSoundButton } from './sound.ts';
import { afterRender, inLobby, noteTable, resetFx } from './fx.ts';
import { flush, setContext, startTelemetry, track } from './telemetry.ts';
import { GAME_UIS, type TableCtx } from './games.ts';
import { settingsForm, settingsSummary } from './settings.ts';

type Mode =
  | { readonly kind: 'home' }
  | { readonly kind: 'online'; readonly client: ClientSession; readonly link: SocketLink; readonly name: string; readonly role: Role }
  | { readonly kind: 'hotseat'; readonly host: HostSession; readonly clients: readonly ClientSession[]; shownSeat: Seat | null; readonly pins: string[] | null; readonly resets: number[]; resetting: Seat | null }
  /** Pass & play setup: each player in turn types their name and a private PIN (FR-UX-05). */
  | { readonly kind: 'pins'; readonly count: number; readonly gameId: string; readonly names: string[]; readonly pins: string[] };

/** Build flag: true for the standalone pass-and-play page (no host to join). */
declare const __HOTSEAT_ONLY__: boolean;
const HOTSEAT_ONLY = typeof __HOTSEAT_ONLY__ !== 'undefined' && __HOTSEAT_ONLY__;

const root = document.getElementById('app') as HTMLElement;
const params = new URLSearchParams(location.search);
/** Room code from the invite link or typed on the join screen; never part of the Wi-Fi broadcast. */
let roomFromUrl = params.get('room')?.toUpperCase() ?? null;
const localUi = new Map<string, unknown>();
let mode: Mode = { kind: 'home' };
let joinUrls: string[] = [];
/** Pass & play settings chosen on the home screen, by game id (memory only, per ADR-0010). */
const hotseatSettings = new Map<string, Record<string, OptionValue>>();
let renderQueued = false;
let chat: ChatPanel | null = null;
/** Clears per-turn UI state but keeps per-player preferences (keys starting "keep:"), e.g. hand order. */
function clearLocalUi(all = false): void {
  for (const k of [...localUi.keys()]) if (all || !k.startsWith('keep:')) localUi.delete(k);
}

// The iOS app puts its APNs token here (now or once iOS hands it over); each seat it joins gets it
// so the table can notify this phone when it's their turn while away (FR-PUSH-01).
let pushSent = '';
function sendPushToken(): void {
  const token = (globalThis as { CAMPFIRE_PUSH?: string }).CAMPFIRE_PUSH;
  if (!token || mode.kind !== 'online' || mode.link.status !== 'open') return;
  const { id, role } = mode.client.snapshot;
  if (!id || role !== 'player' || pushSent === `${id}:${token}`) return;
  pushSent = `${id}:${token}`;
  mode.client.push(token);
}
(globalThis as { campfirePush?: (t: string) => void }).campfirePush = (t) => {
  (globalThis as { CAMPFIRE_PUSH?: string }).CAMPFIRE_PUSH = t;
  sendPushToken();
};

function render(): void {
  sendPushToken();
  if (renderQueued) return;
  renderQueued = true;
  queueMicrotask(() => {
    renderQueued = false;
    try {
      root.replaceChildren(view());
      afterRender(root);
    } catch (e) {
      // Never leave a blank screen: say so, report it, and offer a reload (FR-OBS-02).
      track('render-error', { message: String((e as Error).message).slice(0, 500), stack: String((e as Error).stack ?? '').slice(0, 2000), mode: mode.kind });
      flush(true);
      root.replaceChildren(h('main.center', { 'data-testid': 'render-error' }, h('h2', {}, 'Something went wrong on this screen'), h('button.primary', { onclick: () => location.reload() }, 'Reload')));
    }
  });
}

const local: TableCtx['local'] = {
  get: <T>(k: string) => localUi.get(k) as T | undefined,
  set: (k, v) => {
    localUi.set(k, v);
    render();
  },
};

// ---------------- reconnect token in URL hash (no browser storage, per ADR-0010) ----------------
function readHash(): { t: string; n: string; r: Role } | null {
  const p = new URLSearchParams(location.hash.slice(1));
  const t = p.get('t');
  const n = p.get('n');
  const r = p.get('r');
  return t && n && (r === 'player' || r === 'spectator') ? { t, n, r } : null;
}
function writeHash(token: string, name: string, role: Role): void {
  const p = new URLSearchParams({ t: token, n: name, r: role });
  history.replaceState(null, '', `${location.pathname}${location.search}#${p.toString()}`);
}

// ---------------- modes ----------------
function goOnline(name: string, role: Role, token?: string): void {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const room = roomFromUrl ? `?room=${encodeURIComponent(roomFromUrl)}` : '';
  // 403 = wrong or expired room code (e.g. the host restarted); anything else is worth retrying.
  // A relayed table whose phone host is locked or in another app answers hostAway (FR-NET-26).
  const probe = () =>
    fetch(`/api/info${room}`, { cache: 'no-store' }).then(async (r) => {
      hostAway = r.ok && !!((await r.json().catch(() => ({}))) as { hostAway?: boolean }).hostAway;
      return r.status !== 403;
    });
  // A phone host serves its WebSocket on its own port and checks the code as a subprotocol (ADR-0014).
  const wsBase = (globalThis as { CAMPFIRE_WS?: string }).CAMPFIRE_WS ?? `${proto}://${location.host}`;
  const link = connectSocket(`${wsBase}/ws${room}`, probe, roomFromUrl ? [`room.${roomFromUrl}`] : []);
  const client = createClientSession(link);
  chat?.remove();
  chat = mountChat(client);
  link.onOpen(() => client.hello(name, role, client.snapshot.token ?? token));
  setContext({ room: roomFromUrl, role });
  track('join', { name: name.slice(0, 24), resumed: !!token });
  let statusSince = Date.now();
  link.onStatus((st) => {
    track('link', { status: st, after: Date.now() - statusSince });
    statusSince = Date.now();
    if (st !== 'open') flush(true);
    render();
  });
  let seen = { error: '', rejection: '', game: '', outcome: false, seat: -2 };
  client.subscribe((s) => {
    const err = s.lastError ? `${s.lastError.code}:${s.lastError.message}` : '';
    if (err && err !== seen.error) track('host-error', { code: s.lastError?.code, message: s.lastError?.message });
    const rej = s.lastRejection ? `${s.lastRejection.reason}:${s.lastRejection.detail ?? ''}` : '';
    if (rej && rej !== seen.rejection) track('rejected', { reason: s.lastRejection?.reason, detail: s.lastRejection?.detail });
    const game = s.game?.gameId ?? '';
    if (game !== seen.game) track('game', { gameId: game || null });
    if (!!s.game?.outcome !== seen.outcome && s.game?.outcome) track('outcome', { gameId: game, winners: s.game.outcome.winners });
    if ((s.game?.seat ?? -1) !== seen.seat) setContext({ seat: s.game?.seat ?? null, game: game || null });
    seen = { error: err, rejection: rej, game, outcome: !!s.game?.outcome, seat: s.game?.seat ?? -1 };
    chat?.update(s);
    if (s.token) writeHash(s.token, name, role);
    render();
  });
  mode = { kind: 'online', client, link, name, role };
  void fetch(`/api/info${room}`)
    .then((r) => r.json() as Promise<{ joinUrls: string[] }>)
    .then((info) => {
      joinUrls = info.joinUrls;
      render();
    })
    .catch(() => {});
  render();
}

function goHotseat(names: string[], gameId: string, pins: readonly string[] | null = null): void {
  inLobby();
  setContext({ room: 'hotseat', game: gameId });
  track('hotseat-start', { gameId, players: names.length, pins: !!pins });
  const host = createHostSession({
    registry: createDefaultRegistry(),
    newId: () => randomId(),
    newSeed: () => randomId(),
    roomCode: 'LOCAL',
  });
  const clients = names.map((n) => {
    const c = createClientSession(connectInMemory(host));
    c.hello(n, 'player');
    c.subscribe(render);
    return c;
  });
  for (const [id, options] of hotseatSettings) clients[0]?.configure(id, options);
  clients[0]?.start(gameId);
  mode = { kind: 'hotseat', host, clients, shownSeat: null, pins: pins ? [...pins] : null, resets: names.map(() => 0), resetting: null };
  render();
}

function leave(): void {
  resetFx();
  if (mode.kind === 'online') mode.link.close();
  chat?.remove();
  chat = null;
  history.replaceState(null, '', location.pathname + location.search);
  mode = { kind: 'home' };
  clearLocalUi(true);
  render();
}

// ---------------- views ----------------
function view(): HTMLElement {
  switch (mode.kind) {
    case 'home':
      return homeView();
    case 'online':
      return onlineView(mode);
    case 'hotseat':
      return hotseatView(mode);
    case 'pins':
      return pinSetupView(mode);
  }
}

// Hide a pass & play hand whenever the app is switched away or the screen locks, so the
// app switcher or the next person to pick up the phone sees nothing (FR-UX-05).
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden' && mode.kind === 'hotseat' && mode.pins) {
    mode.shownSeat = null;
    render();
  }
});

let usePins = true;

function pinSetupView(m: Extract<Mode, { kind: 'pins' }>): HTMLElement {
  const i = m.names.length;
  const nameIn = h('input', { 'data-testid': 'pin-name', placeholder: `Player ${i + 1}`, maxlength: 24, autocomplete: 'off' }) as HTMLInputElement;
  const pinIn = h('input', { 'data-testid': 'pin-new', type: 'password', inputmode: 'numeric', pattern: '[0-9]*', maxlength: 8, placeholder: 'PIN (optional, 4+ digits)', autocomplete: 'off' }) as HTMLInputElement;
  const err = h('div.error', { 'data-testid': 'pin-error' });
  const next = () => {
    const pin = pinIn.value.trim();
    // A blank PIN leaves that player's hand unlocked (FR-UX-14).
    if (pin && !/^\d{4,8}$/.test(pin)) {
      err.textContent = 'A PIN is 4 to 8 digits, or leave it blank.';
      pinIn.focus();
      return;
    }
    m.names.push(nameIn.value.trim().slice(0, 24) || `Player ${i + 1}`);
    m.pins.push(pin);
    if (m.names.length === m.count) goHotseat(m.names, m.gameId, m.pins.some(Boolean) ? m.pins : null);
    else render();
  };
  pinIn.addEventListener('keydown', (e) => e.key === 'Enter' && next());
  return h(
    'main.handoff',
    { 'data-testid': 'pin-setup' },
    h('h2', {}, i === 0 ? 'Player 1, who are you?' : `Pass to player ${i + 1}`),
    h('p.muted', {}, `Player ${i + 1} of ${m.count}: type your name. Add a PIN only you know to lock your cards, or leave it blank.`),
    nameIn,
    pinIn,
    err,
    h('button.primary.big', { 'data-testid': 'pin-next', onclick: next }, i + 1 === m.count ? 'Start the game' : 'Next player'),
    h('button.link', { onclick: leave }, 'Cancel'),
  );
}

let homeEl: HTMLElement | null = null;
function homeView(): HTMLElement {
  if (homeEl) return homeEl;
  const nameInput = h('input', { id: 'name', 'data-testid': 'name', maxlength: 24, placeholder: 'Your name', autocomplete: 'nickname' }) as HTMLInputElement;
  const err = h('div.error', { 'data-testid': 'home-error' });
  // Public tables refuse room info without a code; then ask for it (FR-NET-14).
  const codeInput = h('input', { 'data-testid': 'room-code-input', placeholder: 'Room code', autocapitalize: 'characters', autocomplete: 'off', maxlength: 8, hidden: true }) as HTMLInputElement;
  if (!roomFromUrl && !HOTSEAT_ONLY) {
    void fetch('/api/info', { cache: 'no-store' })
      .then((r) => {
        if (r.status === 403) codeInput.hidden = false;
      })
      .catch(() => {});
  }
  const join = (role: Role) => {
    const n = nameInput.value.trim();
    if (!n) {
      err.textContent = 'Enter a name first.';
      nameInput.focus();
      return;
    }
    if (!codeInput.hidden) {
      const code = codeInput.value.trim().toUpperCase();
      if (!code) {
        err.textContent = 'Enter the room code from the host’s screen.';
        codeInput.focus();
        return;
      }
      roomFromUrl = code;
      history.replaceState(null, '', `${location.pathname}?room=${encodeURIComponent(code)}${location.hash}`);
    }
    goOnline(n, role);
  };  // The keyboard's Go/Return joins as a player (FR-UX-11).
  nameInput.addEventListener('keydown', (e) => e.key === 'Enter' && join('player'));

  const count = h('select', { 'data-testid': 'hs-count' }, [2, 3, 4, 5, 6].map((n) => h('option', { value: n }, `${n} players`))) as HTMLSelectElement;
  const games = createDefaultRegistry().list();
  const settingsBox = h('div', { 'data-testid': 'hs-settings-panel' });
  const showSettings = () => {
    const g = games.find((x) => x.id === gameSel.value)!;
    const specs = g.optionSpecs ?? [];
    settingsBox.replaceChildren(
      specs.length
        ? settingsForm(specs, resolveSettings(g, hotseatSettings.get(g.id)), (key, value) => hotseatSettings.set(g.id, { ...hotseatSettings.get(g.id), [key]: value }))
        : h('p.muted', {}, `${g.name} has no settings.`),
    );
  };
  const gameSel = h('select', { 'data-testid': 'hs-game', onchange: showSettings }, games.map((g) => h('option', { value: g.id }, g.name))) as HTMLSelectElement;
  showSettings();
  const hsErr = h('div.error');
  homeEl = h(
    'main.home',
    {},
    h('h1', {}, 'Campfire'),
    h('p.muted', {}, HOTSEAT_ONLY ? 'Pass one phone around the table. Hands stay hidden between turns.' : 'Card games on everyone’s phone. No app, no internet needed on the local network.'),
    HOTSEAT_ONLY
      ? null
      : h(
      'section.panel',
      {},
      h('h2', {}, roomFromUrl ? `Join room ${roomFromUrl}` : 'Join this table'),
      signInStrip((given) => {
        if (!nameInput.value) nameInput.value = given.slice(0, 24);
      }),
      nameInput,
      codeInput,
      h(
        'div.row',
        {},
        h('button.primary', { 'data-testid': 'join-player', onclick: () => join('player') }, 'Join as player'),
        h('button', { 'data-testid': 'join-spectator', onclick: () => join('spectator') }, 'Table display'),
      ),
      err,
    ),
    h(
      'section.panel',
      // At a phone-hosted table everyone has their own phone; pass & play would only confuse.
      { hidden: 'CAMPFIRE_WS' in globalThis },
      h('h2', {}, 'Pass & play on this device'),
      h('div.row', {}, count, gameSel),
      h('details.settings-box', {}, h('summary', { 'data-testid': 'hs-settings' }, 'Game settings'), settingsBox),
      h(
        'label.check',
        {},
        h('input', { type: 'checkbox', 'data-testid': 'hs-pins', checked: usePins, onchange: (e: Event) => (usePins = (e.target as HTMLInputElement).checked) }),
        ' Enter names (and optional PINs)',
      ),
      h(
        'button',
        {
          'data-testid': 'hs-start',
          onclick: () => {
            const n = Number(count.value);
            const g = games.find((x) => x.id === gameSel.value);
            if (!g || n < g.minSeats || n > g.maxSeats) {
              hsErr.textContent = g && g.minSeats === g.maxSeats ? `${g.name} needs exactly ${g.minSeats} players.` : `${g?.name ?? 'That game'} needs ${g?.minSeats}–${g?.maxSeats} players.`;
              return;
            }
            hsErr.textContent = '';
            if (usePins) {
              mode = { kind: 'pins', count: n, gameId: g.id, names: [], pins: [] };
              render();
            } else {
              goHotseat(Array.from({ length: n }, (_, i) => `Player ${i + 1}`), g.id);
            }
          },
        },
        'Start pass & play',
      ),
      hsErr,
    ),
  );
  return homeEl;
}

const GONE_AFTER = 6;
let hostAway = false;

function onlineView(m: Extract<Mode, { kind: 'online' }>): HTMLElement {
  const s = m.client.snapshot;
  if (m.link.status === 'denied') {
    return h(
      'div.screen',
      { 'data-testid': 'room-denied' },
      h('h2', {}, roomFromUrl ? `Room ${roomFromUrl} isn’t open` : 'This table needs a room code'),
      h('p.muted', {}, 'The code may be mistyped, or the table restarted with a new one. Ask the host for a fresh invite link.'),
    );
  }
  if (s.lastError?.code === 'kicked') {
    // Stop reconnecting and forget the seat token: it no longer opens this table.
    m.link.close();
    history.replaceState(null, '', location.pathname + location.search);
    return h('div.screen', { 'data-testid': 'kicked' }, h('h2', {}, 'You were removed from this table'), h('p.muted', {}, 'The host removed you. Ask them if you think it was a mistake.'));
  }
  // After several failed tries the table has probably gone (host stopped, phone asleep): say so, keep trying (FR-NET-25).
  const gone = m.link.status !== 'open' && m.link.failures >= GONE_AFTER;
  const hostName = s.lobby?.members.find((x) => x.id === s.lobby?.ownerId)?.name;
  const banner = m.link.status !== 'open' && hostAway
    ? h('div.banner.gone', { 'data-testid': 'host-away' }, `Waiting for ${hostName ? `${hostName}’s` : 'the host’s'} phone. It’s hosting this table and may be locked or in another app. This reconnects by itself when it’s back.`)
    : gone
    ? h('div.banner.gone', { 'data-testid': 'table-gone' }, 'Can’t reach this table. The host may have stopped it or their phone may be asleep. Still trying… ', h('a', { href: location.pathname }, 'Leave'))
    : m.link.status !== 'open'
      ? h('div.banner', { 'data-testid': 'reconnecting' }, m.link.status === 'connecting' ? 'Connecting…' : 'Reconnecting…')
      : null;
  if (!s.id || !s.lobby) return h('main.center', {}, banner ?? h('div', {}, 'Joining…'), s.lastError ? h('div.error', {}, s.lastError.message) : null);
  const spectator = s.role === 'spectator';
  const body = s.game
    ? tableView(s.game, m.client, {
        ownerMode: m.client.isOwner(),
        games: s.lobby.games,
        roomCode: s.lobby.roomCode,
        ...(m.client.isOwner() ? { onEnd: () => m.client.end() } : {}),
      })
    : (s.lobby.gameId === null && inLobby(), lobbyView(m.client));
  // Moves made while the link is down would be lost or land late: the table is visible but inert (FR-NET-26).
  const offline = m.link.status !== 'open' ? '.offline' : '';
  return h(`div.app${spectator ? '.spectator' : ''}${offline}`, {}, banner, body, s.lastError ? h('div.toast.error', { 'data-testid': 'error' }, s.lastError.message) : null);
}

/** Results screen: pick another game from a dropdown of those that fit this table (FR-UX-15). */
function switchGame(client: ClientSession, games: readonly GameInfo[], players: number, current: string): HTMLElement | null {
  const fits = games.filter((g) => g.id !== current && players >= g.minSeats && players <= g.maxSeats);
  if (!fits.length) return null;
  const sel = h('select', { 'data-testid': 'switch-game' }, fits.map((g) => h('option', { value: g.id }, g.name))) as HTMLSelectElement;
  return h('div.row.switch-game', {}, sel, h('button', { 'data-testid': 'switch-start', onclick: () => client.start(sel.value) }, 'Play this'));
}

function gameButtons(client: ClientSession, games: readonly GameInfo[], players: number, withSettings = false): HTMLElement {
  const settings = client.snapshot.lobby?.settings ?? {};
  return h(
    'div.games',
    {},
    games.map((g) => {
      const fits = players >= g.minSeats && players <= g.maxSeats;
      const start = h(
        'button.game',
        { 'data-testid': `start-${g.id}`, disabled: !fits, onclick: () => client.start(g.id) },
        h('strong', {}, g.name),
        h('span.muted', {}, `${g.minSeats === g.maxSeats ? g.minSeats : `${g.minSeats}–${g.maxSeats}`} players`),
        g.optionSpecs.length ? h('span.muted.summary', { 'data-testid': `summary-${g.id}` }, settingsSummary(g.optionSpecs, settings[g.id] ?? {})) : null,
      );
      if (!withSettings) return start;
      // Same shape for every game so cards and Settings links line up across a row.
      const link = g.optionSpecs.length
        ? h('button.link', { 'data-testid': `settings-${g.id}`, onclick: () => local.set('settings-for', g.id) }, 'Settings')
        : h('button.link', { style: 'visibility:hidden', tabindex: -1, 'aria-hidden': 'true' }, 'Settings');
      const rules = h('button.link', { 'data-testid': `rules-${g.id}`, onclick: () => local.set('rules-for', g.id) }, 'Rules');
      return h('div.game-entry', {}, start, h('div.entry-links', {}, link, rules));
    }),
  );
}

/** How to play one game (FR-RULES-01): what the app enforces and what the table agrees on. */
function rulesOverlay(games: readonly GameInfo[]): HTMLElement | null {
  const g = games.find((x) => x.id === local.get<string>('rules-for'));
  if (!g) return null;
  return h(
    'div.overlay.rules',
    { 'data-testid': 'rules', onclick: (e: Event) => e.target === e.currentTarget && local.set('rules-for', undefined) },
    h(
      'div.panel.rules-panel',
      {},
      h('h2', {}, `How to play ${g.name}`),
      g.rules.length ? h('ul', {}, g.rules.map((r) => h('li', {}, r))) : h('p.muted', {}, 'No written rules yet.'),
      h('button.primary', { 'data-testid': 'rules-close', onclick: () => local.set('rules-for', undefined) }, 'Got it'),
    ),
  );
}

/** Owner-only settings page for one game; every change goes straight to the host (FR-OPT-06). */
function settingsView(client: ClientSession, g: GameInfo): HTMLElement {
  const values = client.snapshot.lobby?.settings[g.id] ?? {};
  return h(
    'main.lobby',
    { 'data-testid': 'settings-page' },
    h('h2', {}, `${g.name} settings`),
    settingsForm(g.optionSpecs, values, (key, value) => client.configure(g.id, { [key]: value })),
    h('button.primary', { 'data-testid': 'settings-done', onclick: () => local.set('settings-for', undefined) }, 'Done'),
  );
}

let showWifiLinks = false;

// Tell the table when this app/tab goes to the background, so others see "away" (FR-LOBBY-08).
document.addEventListener('visibilitychange', () => {
  if (mode.kind === 'online' && mode.link.status === 'open') mode.client.presence(document.visibilityState === 'hidden');
});

/** "online", "away", or "last seen 5m" for a member (FR-LOBBY-08). */
function presenceOf(m: { connected: boolean; away: boolean; lastSeen: number }): { cls: string; text: string } {
  if (m.connected && !m.away) return { cls: 'on', text: 'online' };
  if (m.connected) return { cls: 'away', text: 'away' };
  const age = shortAge(m.lastSeen, Date.now());
  return { cls: 'off', text: age ? (age === 'now' ? 'just left' : `seen ${age}${/m|h$/.test(age) ? ' ago' : ''}`) : 'offline' };
}

/**
 * The invite link people should use (the https one: it works anywhere and opens the app), with a
 * share button right next to it; the Wi‑Fi addresses (work with no internet) behind a toggle.
 */
function inviteBox(): HTMLElement {
  const primary = joinUrls.find((u) => u.startsWith('https://')) ?? joinUrls[0] ?? '';
  const wifi = joinUrls.filter((u) => u !== primary);
  const status = h('span.muted', { 'data-testid': 'invite-status' });
  const share = async () => {
    const nav = navigator as Navigator & { share?: (d: { url: string; title: string }) => Promise<void> };
    try {
      if (nav.share) await nav.share({ url: primary, title: 'Join my Campfire table' });
      else {
        await navigator.clipboard.writeText(primary);
        status.textContent = 'Link copied';
      }
    } catch {
      // Share sheet dismissed or clipboard blocked: the link is still on screen to copy by hand.
    }
  };
  return h(
    'div.join-urls',
    { 'data-testid': 'invite' },
    h('span.muted', {}, 'Invite link'),
    h('div.row.invite-row', {}, h('code', { 'data-testid': 'invite-link' }, primary.replace(/^https?:\/\//, '')), h('button.primary', { 'data-testid': 'invite-share', onclick: () => void share() }, 'Share'), status),
    wifi.length
      ? h(
          'label.check',
          {},
          h('input', { type: 'checkbox', 'data-testid': 'invite-wifi-toggle', checked: showWifiLinks, onchange: (e: Event) => ((showWifiLinks = (e.target as HTMLInputElement).checked), render()) }),
          ' Show Wi‑Fi address (works with no internet)',
        )
      : null,
    showWifiLinks ? wifi.map((u) => h('code', { 'data-testid': 'invite-wifi' }, u.replace(/^https?:\/\//, ''))) : null,
  );
}

function lobbyView(client: ClientSession): HTMLElement {
  const s = client.snapshot;
  const lobby = s.lobby!;
  const players = lobby.members.filter((x) => x.role === 'player' && x.connected);
  const owner = lobby.members.find((x) => x.id === lobby.ownerId);
  const editing = client.isOwner() ? lobby.games.find((g) => g.id === local.get<string>('settings-for')) : undefined;
  if (editing) return settingsView(client, editing);
  return h(
    'main.lobby',
    {},
    client.isOwner()
      ? h('input.room-name', {
          'data-testid': 'room-name-input',
          value: lobby.roomName,
          maxlength: 32,
          'aria-label': 'Table name',
          onchange: (e: Event) => {
            const v = (e.target as HTMLInputElement).value.trim();
            if (v && v !== lobby.roomName) client.rename(v);
          },
        })
      : h('h2.room-name', { 'data-testid': 'room-name' }, lobby.roomName),
    h('div.room', {}, h('span.muted', {}, 'Room code'), h('span.code', { 'data-testid': 'room-code' }, lobby.roomCode)),
    joinUrls.length ? inviteBox() : null,
    h(
      'ul.members',
      { 'data-testid': 'members' },
      lobby.members.map((x) =>
        h(
          `li${x.connected ? '' : '.offline'}`,
          { 'data-testid': `member-${x.name}` },
          h(`span.dot.${presenceOf(x).cls}`, {}),
          x.name,
          x.verified ? h('span.tag.verified', { 'data-testid': `verified-${x.name}`, title: 'Signed in with Google' }, '✓ Google') : null,
          h('span.muted.presence', { 'data-testid': `presence-${x.name}` }, presenceOf(x).text),
          x.id === s.id ? h('span.tag', {}, 'you') : null,
          x.id === lobby.ownerId ? h('span.tag', {}, 'host') : null,
          x.role === 'spectator' ? h('span.tag', {}, 'display') : null,
          client.isOwner() && x.id !== s.id
            ? confirmButton('button.link.remove', { 'data-testid': `kick-${x.name}` }, 'Remove', `Tap again to remove ${x.name}`, () => client.kick(x.id))
            : null,
        ),
      ),
    ),
    client.isOwner()
      ? h('section', {}, h('h2', {}, 'Pick a game'), gameButtons(client, lobby.games, players.length, true))
      : h('p.muted', { 'data-testid': 'waiting' }, `Waiting for ${owner?.name ?? 'the host'} to pick a game…`),
    rulesOverlay(lobby.games),
  );
}

interface TableOpts {
  readonly ownerMode: boolean;
  readonly games: readonly GameInfo[];
  readonly roomCode: string;
  readonly onHome?: () => void;
  /** Session allowed to start games (the room owner). Hot-seat routes starts through seat 0. */
  readonly starter?: ClientSession;
  /** Owner-only: end the game for everyone and return to the lobby. */
  readonly onEnd?: () => void;
  /** Pass & play: everyone shares this phone, so results use names, never "You" (FR-UX-12). */
  readonly sameDevice?: boolean;
}

/** The finished game whose results were put away to look at the final board (FR-UX-12). */
let outcomeHidden = '';

function tableView(msg: StateMessage, client: ClientSession, opts: TableOpts): HTMLElement {
  const ui = GAME_UIS[msg.gameId];
  const gameName = opts.games.find((g) => g.id === msg.gameId)?.name ?? msg.gameId;
  const mySeat = msg.seat;
  noteTable(msg.gameId, mySeat, msg.seq);
  const ctx: TableCtx = {
    msg,
    names: msg.seatNames,
    mySeat,
    you: opts.sameDevice ? null : mySeat,
    myTurn: mySeat !== null && (msg.waitingOn ?? msg.activeSeats).includes(mySeat) && !msg.outcome,
    act: (a) => {
      // Each move also carries the latest logs (type only: never the cards themselves).
      track('act', { action: a.type, seq: msg.seq, game: msg.gameId });
      client.act(a);
      flush();
    },
    local,
  };
  const seats = h(
    'div.seats',
    { 'data-testid': 'seats' },
    msg.seatNames.map((n, s) =>
      h(
        `div.seat${(msg.waitingOn ?? msg.activeSeats).includes(s) ? '.active' : ''}${s === mySeat ? '.me' : ''}`,
        { 'data-testid': `seat-${s}` },
        h('strong', {}, (() => {
          const member = client.snapshot.lobby?.members.find((m) => m.seat === s);
          return [member && !opts.starter ? h(`span.dot.${presenceOf(member).cls}`, { title: presenceOf(member).text }) : null, s === mySeat && !opts.sameDevice ? `${n} (you)` : n];
        })()),
        h('span.muted', {}, ui ? ui.seatInfo(ctx, s) : ''),
        (() => {
          const member = client.snapshot.lobby?.members.find((m) => m.seat === s);
          const p = member && !opts.starter ? presenceOf(member) : null;
          return p && p.cls !== 'on' ? h(`span.presence.${p.cls}`, { 'data-testid': `seat-presence-${s}` }, p.text) : null;
        })(),
      ),
    ),
  );
  const waitingOn = (msg.waitingOn ?? msg.activeSeats).map((s) => msg.seatNames[s]).join(' & ');
  const status = msg.outcome
    ? ''
    : ctx.myTurn
      ? 'Your turn'
      : mySeat === null
        ? `${waitingOn}’s turn`
        : `Waiting for ${waitingOn}…`;
  const rejection = client.snapshot.lastRejection;
  return h(
    'main.table',
    { 'data-game': msg.gameId },
    h(
      'header.bar',
      {},
      h('strong', {}, gameName),
      h('span.muted', {}, opts.roomCode),
      h('button.link', { 'data-testid': 'table-rules', onclick: () => local.set('rules-for', msg.gameId) }, 'Rules'),
      opts.onEnd && !msg.outcome ? confirmButton('button.link', { 'data-testid': 'end-game' }, 'End game', 'Tap again to end', () => opts.onEnd?.()) : null,
      opts.onHome ? h('button.link', { onclick: opts.onHome }, 'Quit') : null,
    ),
    seats,
    h('div.felt', {}, ui ? ui.render(ctx) : h('div', {}, `No renderer for ${msg.gameId}`)),
    h('footer.status', { 'data-testid': 'status' }, status, rejection ? h('span.error', { 'data-testid': 'rejection' }, ` ${rejection.detail ?? rejection.reason}`) : null),
    msg.outcome ? outcomeView(msg, client, opts) : null,
    rulesOverlay(opts.games),
    jumpButtons(),
  );
}

/**
 * Floating "Table" / "Hand" jumps (FR-UX-10): on a phone the table and your hand rarely fit on one
 * screen, so these skip the scrolling. Shown only when the page is taller than the screen.
 */
function jumpButtons(): HTMLElement {
  const hand = () => [...document.querySelectorAll<HTMLElement>('.felt .hand')].pop();
  const toHand = () => {
    const el = hand();
    if (el) window.scrollTo({ top: el.getBoundingClientRect().bottom + window.scrollY - window.innerHeight + 96, behavior: 'smooth' });
  };
  // One small button that points the other way: to your hand from the table, to the table from your hand.
  const table = h('button', { 'data-testid': 'jump-table', onclick: () => window.scrollTo({ top: 0, behavior: 'smooth' }) }, '▲ Table');
  const mine = h('button', { 'data-testid': 'jump-hand', onclick: toHand }, '▼ Hand');
  const bar = h('nav.jump', { 'data-testid': 'jump', 'aria-label': 'Jump to' }, table, mine);
  const paint = () => {
    const el = hand();
    if (!bar.isConnected) return window.removeEventListener('scroll', paint);
    bar.hidden = !el || document.documentElement.scrollHeight <= window.innerHeight + 40;
    // At the top you are looking at the table; anywhere lower, you are down at your hand.
    const down = window.scrollY > 40;
    table.hidden = !down;
    mine.hidden = down;
  };
  bar.hidden = true;
  requestAnimationFrame(paint);
  window.addEventListener('scroll', paint, { passive: true });
  return bar;
}

function outcomeView(msg: StateMessage, client: ClientSession, opts: TableOpts): HTMLElement {
  const o = msg.outcome!;
  const winners = o.winners.map((s) => (s === msg.seat && !opts.sameDevice ? 'You' : msg.seatNames[s]));
  const headline = o.draw ? 'It’s a draw' : winners.length === 1 ? `${winners[0]} ${winners[0] === 'You' ? 'win' : 'wins'}!` : `${winners.join(' & ')} win!`;
  const sub = msg.gameId === 'old-maid' && !o.draw ? `${msg.seatNames.find((_, i) => !o.winners.includes(i))} is left holding the Old Maid.` : '';
  const players = msg.seatNames.length;
  const key = `${msg.gameId}:${msg.seq}`;
  if (outcomeHidden === key) {
    // Results put away: the final board stays visible with a bar to bring them back.
    return h(
      'div.outcome-bar',
      { 'data-testid': 'outcome-bar' },
      h('strong', {}, headline),
      h('button.primary', { 'data-testid': 'outcome-show', onclick: () => ((outcomeHidden = ''), render()) }, 'Results'),
    );
  }
  return h(
    'div.overlay',
    { 'data-testid': 'outcome' },
    h(
      'div.dialog',
      {},
      h('button.link.outcome-board', { 'data-testid': 'outcome-board', onclick: () => ((outcomeHidden = key), render()) }, 'See the board'),
      h('h2', { 'data-testid': 'outcome-text' }, headline),
      sub ? h('p', {}, sub) : null,
      opts.ownerMode
        ? h(
            'div',
            {},
            h('button.primary', { 'data-testid': 'rematch', onclick: () => (opts.starter ?? client).start(msg.gameId) }, 'Play again'),
            switchGame(opts.starter ?? client, opts.games, players, msg.gameId),
            opts.onEnd ? h('button.link', { 'data-testid': 'to-lobby', onclick: opts.onEnd }, 'Back to lobby') : null,
          )
        : h('p.muted', {}, 'Waiting for the host to start the next game…'),
      opts.onHome ? h('button.link', { onclick: opts.onHome }, 'Back to start') : null,
    ),
  );
}

/** Focus a hand-off button once it is on the page, so Enter or Space on a computer taps it (FR-UX-16). */
function focusSoon(el: HTMLElement): HTMLElement {
  queueMicrotask(() => el.focus({ preventScroll: true }));
  return el;
}

function hotseatView(m: Extract<Mode, { kind: 'hotseat' }>): HTMLElement {
  const any = m.clients[0];
  const g = any?.snapshot.game;
  if (!any || !g) return h('main.center', {}, any?.snapshot.lastError?.message ?? 'Starting…');
  const ui = GAME_UIS[g.gameId];
  // Focus the first seat that must act; after the game, keep the last shown seat.
  const focus: Seat = g.outcome ? (m.shownSeat ?? 0) : ((g.waitingOn ?? g.activeSeats)[0] ?? m.shownSeat ?? 0);
  const client = m.clients[focus] as ClientSession;
  const state = client.snapshot.game as StateMessage;
  const needsHandoff = !!ui?.privateHands && !g.outcome && m.shownSeat !== focus && m.clients.length > 1;
  const games = any.snapshot.lobby?.games ?? [];
  // Open boards: the table stays visible, with a "hand the device to" card over it when the turn
  // moves to someone else (not at the start of a game) (FR-UX-13).
  const nextUp = !ui?.privateHands && !g.outcome && m.shownSeat !== null && m.shownSeat !== focus && m.clients.length > 1;
  if ((!ui?.privateHands && !nextUp) || g.outcome) m.shownSeat = focus;
  const table = tableView(state, client, { ownerMode: true, games, roomCode: 'Pass & play', onHome: leave, starter: any, sameDevice: true });
  if (nextUp) {
    const dismiss = () => {
      m.shownSeat = focus;
      render();
    };
    return h(
      'div.app.hotseat',
      {},
      table,
      h(
        'div.overlay.nextup',
        { 'data-testid': 'nextup', onclick: dismiss },
        h('div.dialog', {}, h('p.muted', {}, 'Next up'), h('h2', {}, `Hand the device to ${state.seatNames[focus]}`), focusSoon(h('button.primary.big', { 'data-testid': 'nextup-ready' }, `I’m ${state.seatNames[focus]}`))),
      ),
    );
  }
  if (!needsHandoff) return h('div.app.hotseat', {}, table);
  const locked = !!m.pins?.[focus];
  return h(
    'div.app.hotseat',
    {},
    h(
      'div.handoff',
      { 'data-testid': 'handoff' },
      h('h2', {}, `Pass to ${state.seatNames[focus]}`),
      h('p.muted', {}, locked ? 'Only they can open their hand.' : 'Everyone else look away.'),
      m.pins && m.resets.some((n) => n > 0)
        ? h(
            'div.banner',
            { 'data-testid': 'pin-resets' },
            m.resets.flatMap((n, s2) => (n > 0 ? [`${state.seatNames[s2]}’s PIN was reset${n > 1 ? ` ${n} times` : ''}`] : [])).join(' · '),
          )
        : null,
      locked ? (m.resetting === focus ? pinReset(m, focus, state.seatNames[focus] ?? 'Player') : pinGate(m, focus)) : null,
      locked
        ? null
        : focusSoon(h(
            'button.primary.big',
            {
              'data-testid': 'handoff-ready',
              onclick: () => {
                m.shownSeat = focus;
                clearLocalUi();
                render();
              },
            },
            `I’m ${state.seatNames[focus]} — show my cards`,
          )),
    ),
  );
}

/**
 * Forgotten PIN: whoever holds the phone may set a new one without the old PIN, but the reset is
 * announced on every later hand-off, so a quiet peek can't go unnoticed.
 */
function pinReset(m: Extract<Mode, { kind: 'hotseat' }>, seat: Seat, who: string): HTMLElement {
  const input = h('input', { 'data-testid': 'pin-reset-new', type: 'password', inputmode: 'numeric', pattern: '[0-9]*', maxlength: 8, placeholder: 'New PIN', autocomplete: 'off' }) as HTMLInputElement;
  const err = h('div.error');
  const save = () => {
    const pin = input.value.trim();
    if (!/^\d{4,8}$/.test(pin)) {
      err.textContent = 'Choose a PIN of 4 to 8 digits.';
      return;
    }
    (m.pins as string[])[seat] = pin;
    m.resets[seat] = (m.resets[seat] ?? 0) + 1;
    m.resetting = null;
    render();
  };
  input.addEventListener('keydown', (e) => e.key === 'Enter' && save());
  queueMicrotask(() => input.focus());
  return h(
    'div.pin-gate',
    {},
    h('p', {}, `${who}, choose a new PIN. Everyone will be told it was reset.`),
    input,
    h('button.primary.big', { 'data-testid': 'pin-reset-save', onclick: save }, 'Save new PIN'),
    h('button.link', { onclick: () => ((m.resetting = null), render()) }, 'Cancel'),
    err,
  );
}

function pinGate(m: Extract<Mode, { kind: 'hotseat' }>, seat: Seat): HTMLElement {
  const input = h('input', { 'data-testid': 'pin-enter', type: 'password', inputmode: 'numeric', pattern: '[0-9]*', maxlength: 8, placeholder: 'Your PIN', autocomplete: 'off' }) as HTMLInputElement;
  const err = h('div.error', { 'data-testid': 'pin-wrong' });
  const open = () => {
    if (input.value.trim() !== m.pins?.[seat]) {
      err.textContent = 'Wrong PIN.';
      input.value = '';
      input.focus();
      return;
    }
    m.shownSeat = seat;
    clearLocalUi();
    render();
  };
  input.addEventListener('keydown', (e) => e.key === 'Enter' && open());
  queueMicrotask(() => input.focus());
  return h(
    'div.pin-gate',
    {},
    input,
    h('button.primary.big', { 'data-testid': 'pin-open', onclick: open }, 'Show my cards'),
    err,
    h('button.link', { 'data-testid': 'pin-forgot', onclick: () => ((m.resetting = seat), render()) }, 'Forgot PIN? Reset it'),
  );
}

// ---------------- boot ----------------
startTelemetry();
mountTextSizeButton();
mountSoundButton();
// Invite links (https on the relay domain) work from anywhere in a browser through the relay;
// the `lan` hint is only used by the iOS app, which can check whether it's on the host's Wi‑Fi.
const saved = HOTSEAT_ONLY ? null : readHash();
if (saved) goOnline(saved.n, saved.r, saved.t);
else render();
